/*
  Gaia Iluka — Background video playback (shared)
  File: assets/gi-bg-video.js

  Drives every muted, looping, inline autoplay background video in the
  theme. Any <video data-gi-bg-video data-src="..."> is picked up — the
  Island chapter's olive-branch clip (sections/gi-origin.liquid) and the
  Family chapter's pattern animation (sections/gi-home-family.liquid)
  both use it. This was gi-origin-video.js, named for the one section
  that had it; a second background video made the name wrong rather
  than the code, so the file was renamed and the selector generalised.
  All the script decides is WHEN a clip plays.

  WHY A SCRIPT AT ALL, INSTEAD OF A PLAIN src + autoplay: two reasons,
  both about not paying for footage nobody is looking at.

  (1) The <video> ships with no src (the section puts the real URL in
  data-src, preload="none"), so a visitor who never scrolls to that
  chapter never fetches the file. The poster layer behind it carries the
  composition until then.

  (2) An offscreen autoplaying video keeps decoding frames — a real cost
  on laptops and phones for something nobody can see. This pauses it
  when it leaves the viewport and resumes on the way back.

  REDUCED MOTION: the clip is never loaded or played at all; the CSS
  hides the <video> and the poster frame is the whole picture. Checked
  live, not just at load, so toggling the OS setting mid-session takes
  effect without a reload — matching how the previous scroll version
  handled its own media queries.

  play() REJECTIONS ARE EXPECTED, NOT ERRORS: a browser may refuse
  autoplay (power saving, an unusual autoplay policy, a background tab).
  The returned promise is caught and ignored — the poster stays visible
  underneath, which is a perfectly good fallback, and nothing is logged
  because there is no defect to report.

  OPTIONAL data-src-sm="…": a smaller rendition of the same clip, used
  at or below 749px. A phone cannot resolve a 1080p background frame —
  it is scaled down to a few hundred CSS pixels and sits behind a scrim
  — so serving one is megabytes spent on detail no phone screen can
  show. Desktop keeps whatever data-src names, unchanged.

  OPTIONAL data-min-width="750": below that viewport width the clip is
  never fetched and never played, exactly as under reduced motion — the
  poster is the whole picture. For the hero film this is the difference
  between a phone downloading several megabytes of video before it can
  read the headline and downloading none. Opt-in per element: a <video>
  without the attribute behaves exactly as it always has.
*/
(function () {
  'use strict';

  /* Two sections can each include this file. Identical script URLs are
     cached but still re-executed, which would give every clip a second
     controller and a second observer. */
  if (window.__giBgVideoLoaded) return;
  window.__giBgVideoLoaded = true;

  var VIDEO_SELECTOR = '[data-gi-bg-video]';

  var instances = typeof WeakMap === 'function' ? new WeakMap() : null;

  function createController(video) {
    var src = video.getAttribute('data-src');
    if (!src) return null;

    var motionQuery = window.matchMedia('(prefers-reduced-motion: no-preference)');

    /* Watched live rather than read once, so rotating a phone or resizing
       a window settles into the right behaviour without a reload — the
       same reason the motion query is a listener and not a boolean. */
    var minWidth = parseInt(video.getAttribute('data-min-width'), 10);
    var widthQuery = minWidth > 0
      ? window.matchMedia('(min-width: ' + minWidth + 'px)')
      : null;

    var io = null;
    var loaded = false;

    /* Resolved at load time, not at init: a clip that is never scrolled
       to is never fetched, so by the time this runs the viewport is
       whatever the visitor actually has. Once a file is loaded it is
       kept — swapping renditions mid-session would re-download a clip
       the visitor is already watching to no visible benefit. */
    function chooseSrc() {
      var small = video.getAttribute('data-src-sm');
      if (small && window.matchMedia('(max-width: 749px)').matches) return small;
      return src;
    }

    function load() {
      if (loaded) return;
      loaded = true;
      video.src = chooseSrc();
      video.load();
    }

    function play() {
      load();
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
    }

    function pause() {
      if (loaded && !video.paused) video.pause();
    }

    function observe() {
      if (io) return;
      if (typeof IntersectionObserver !== 'function') {
        play();
        return;
      }
      io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) play();
          else pause();
        }
      }, { rootMargin: '200px 0px 200px 0px' });
      io.observe(video);
    }

    function unobserve() {
      if (!io) return;
      io.disconnect();
      io = null;
    }

    function allowed() {
      if (!motionQuery.matches) return false;
      if (widthQuery && !widthQuery.matches) return false;
      return true;
    }

    function evaluate() {
      if (allowed()) {
        observe();
      } else {
        unobserve();
        pause();
      }
    }

    function addMediaListener(mq, handler) {
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else if (mq.addListener) mq.addListener(handler);
    }
    function removeMediaListener(mq, handler) {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    }

    addMediaListener(motionQuery, evaluate);
    if (widthQuery) addMediaListener(widthQuery, evaluate);
    evaluate();

    return {
      destroy: function () {
        unobserve();
        pause();
        removeMediaListener(motionQuery, evaluate);
        if (widthQuery) removeMediaListener(widthQuery, evaluate);
      }
    };
  }

  function init(video) {
    if (!video || !instances || instances.has(video)) return;
    var controller = createController(video);
    if (controller) instances.set(video, controller);
  }

  function destroy(video) {
    if (!video || !instances) return;
    var controller = instances.get(video);
    if (!controller) return;
    controller.destroy();
    instances.delete(video);
  }

  function initWithin(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var found = scope.matches && scope.matches(VIDEO_SELECTOR)
      ? [scope]
      : scope.querySelectorAll(VIDEO_SELECTOR);
    found.forEach ? found.forEach(init) : Array.prototype.forEach.call(found, init);
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    initWithin(document);
  });

  document.addEventListener('shopify:section:load', function (evt) {
    initWithin(evt.target);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var video = evt.target && evt.target.querySelector
      ? evt.target.querySelector(VIDEO_SELECTOR)
      : null;
    if (video) destroy(video);
  });
})();
